// Browser-side draft of an unfinished Inventory Receiving.
// Stored in localStorage (not sessionStorage) so it survives a closed tab
// and a sign-out; sign-out only clears the session keys.
// One key per user and location so two users on one computer, or one user
// receiving at two locations, never see each other's work.

const PREFIX = "receivingDraft";
const DRAFT_VERSION = 1;

function draftKey(userId, locationId) {
  return `${PREFIX}:${userId ?? "anon"}:${locationId ?? "none"}`;
}

export function hasDraftContent({ items, orderNumber, vendorName } = {}) {
  return (
    (Array.isArray(items) && items.length > 0) ||
    Boolean(String(orderNumber ?? "").trim()) ||
    Boolean(String(vendorName ?? "").trim())
  );
}

// A stored row is usable only when it still has the barcode the page keys
// rows by; anything else (hand-edited storage, a future shape change) is
// dropped so it can neither render as an empty row nor be posted on Complete.
function isDraftItem(item) {
  return (
    item !== null &&
    typeof item === "object" &&
    typeof item.product_upc_code === "string" &&
    item.product_upc_code.length > 0 &&
    item.product !== null &&
    typeof item.product === "object"
  );
}

// The part of a draft the user can change, serialized for equality checks.
export function draftContentKey(draft) {
  return JSON.stringify({
    items: draft.items ?? [],
    orderNumber: draft.orderNumber ?? "",
    vendorName: draft.vendorName ?? "",
  });
}

function parseStored(raw) {
  if (!raw) return null;
  const parsed = JSON.parse(raw);
  if (
    !parsed ||
    parsed.version !== DRAFT_VERSION ||
    !Array.isArray(parsed.items)
  ) {
    return null;
  }
  return {
    items: parsed.items.filter(isDraftItem),
    orderNumber: String(parsed.orderNumber ?? ""),
    vendorName: String(parsed.vendorName ?? ""),
    savedAt: typeof parsed.savedAt === "string" ? parsed.savedAt : null,
  };
}

export function loadReceivingDraft(userId, locationId) {
  try {
    const draft = parseStored(
      window.localStorage.getItem(draftKey(userId, locationId))
    );
    return draft && hasDraftContent(draft) ? draft : null;
  } catch {
    return null;
  }
}

export function saveReceivingDraft(userId, locationId, draft) {
  try {
    const key = draftKey(userId, locationId);
    if (!hasDraftContent(draft)) {
      window.localStorage.removeItem(key);
      return;
    }
    // Restoring a draft re-runs the autosave with identical content; keep
    // the stored savedAt then, so the resume toast reports the last edit
    // rather than the last time the page was opened.
    let existing = null;
    try {
      existing = parseStored(window.localStorage.getItem(key));
    } catch {
      existing = null;
    }
    if (existing && draftContentKey(existing) === draftContentKey(draft)) {
      return;
    }
    window.localStorage.setItem(
      key,
      JSON.stringify({
        version: DRAFT_VERSION,
        items: draft.items,
        orderNumber: draft.orderNumber ?? "",
        vendorName: draft.vendorName ?? "",
        savedAt: new Date().toISOString(),
      })
    );
  } catch {
    /* storage unavailable or full: the page keeps working without a draft */
  }
}

export function clearReceivingDraft(userId, locationId) {
  try {
    window.localStorage.removeItem(draftKey(userId, locationId));
  } catch {
    /* storage unavailable */
  }
}

export function formatDraftTime(savedAt) {
  const d = savedAt ? new Date(savedAt) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay ? `today at ${time}` : `${d.toLocaleDateString()} at ${time}`;
}
